import * as Sentry from '@sentry/browser'
import axios from 'axios'
import Utils from '@/services/utils'
import { styleMetaKeys, optionMetaKeys } from '@/constants/meta'
import DesignHub from '@/services/designHub'
import { initializeAxios } from './apiUtils'

const fetchMetaFiltersEndpointMap = {
  [styleMetaKeys.PRICE_ID]: 'prices',
  [styleMetaKeys.PATTERN_ID]: 'patterns',
  [optionMetaKeys.COLOR_ID]: 'colors',
  [optionMetaKeys.DELIVERY_ID]: 'deliveries',
  [optionMetaKeys.ASSORTMENT_ID]: 'assortments'
}

const createMetaFilterEndpointMap = {
  [styleMetaKeys.PRICE_ID]: 'price',
  [styleMetaKeys.PATTERN_ID]: 'pattern'
}

let successCallback = null
let errorCallback = null

/**
 * @returns {string}
 */
const getApiBaseUrl = () => {
  // Local development env variable (only)
  let baseUrl = process.env.VUE_APP_API_BASE_URL

  if (!baseUrl) {
    baseUrl = `https://${
      process.env.VUE_APP_BOARDS_API_SUBDOMAIN
    }.${Utils.getAppDomain()}/`
  }

  return Utils.processBaseUrl(baseUrl)
}

const HTTP = initializeAxios(getApiBaseUrl())

/**
 * @param   {AxiosResponse} response
 *
 * @returns {AxiosResponse}
 */
const responseInterceptorSuccessFunction = response => {
  if (successCallback) {
    successCallback(response)
  }

  return response
}

/**
 * @param   {Error}          error
 *
 * @returns {Promise<Error>}
 */
const responseInterceptorErrorFunction = error => {
  if (errorCallback) {
    errorCallback(error)
  }

  return Promise.reject(error)
}

/**
 * With named functions as interceptors, you may eject them when needed.
 * HTTP.interceptors.response.eject(responseInterceptorErrorFunction)
 */
HTTP.interceptors.response.use(
  responseInterceptorSuccessFunction,
  responseInterceptorErrorFunction
)

/**
 * @param {object} payload
 */
const setCallbacks = payload => {
  successCallback = payload.successCallback
  errorCallback = payload.errorCallback
}

/**
 * @returns {AxiosPromise}
 */
const fetchUserInfo = () => {
  return HTTP.get('/auth/whoami')
}

/**
 * @param   {number}       groupId
 *
 * @returns {AxiosPromise}
 */
const fetchSeasons = groupId => {
  return HTTP.get(`/groups/${groupId}/seasons`)
}

/**
 * @param   {number}       groupId
 *
 * @returns {AxiosPromise}
 */
const fetchInvites = groupId => {
  return HTTP.get('/invitations/', {
    params: {
      group_id: groupId
    }
  })
}

/**
 * @param   {string}       inviteeEmail
 * @param   {number}       groupId
 * @param   {string}       role
 *
 * @returns {AxiosPromise}
 */
const createInvite = (inviteeEmail, groupId, role) => {
  return HTTP.post('/invitations/', {
    to_email: inviteeEmail,
    group_id: groupId,
    role
  })
}

/**
 * @param   {number}       inviteId
 *
 * @returns {AxiosPromise}
 */
const deleteInvite = inviteId => {
  return HTTP.delete(`/invitations/${inviteId}/`)
}

/**
 * @returns {AxiosPromise}
 */
const fetchGroups = () => {
  return HTTP.get('/groups')
}

/**
 * @param   {number}       groupId
 * @param   {number}       seasonId
 *
 * @returns {AxiosPromise}
 */
const fetchProjects = (groupId, seasonId) => {
  return HTTP.get('/projects/', {
    params: {
      group_id: groupId,
      season_id: seasonId
    }
  })
}

/**
 * @param   {number}       projectId
 *
 * @returns {AxiosPromise}
 */
const fetchProject = projectId => {
  return HTTP.get(`/projects/${projectId}/`)
}

/**
 * @param   {number}       projectId
 *
 * @returns {AxiosPromise}
 */
const downloadProjectFeedbackCSV = async projectId => {
  await downloadFile(`/projects/${projectId}/feedback/`, 'ratings', 'csv')
}

/**
 * @param {string} url
 * @param {string} [name]
 * @param {string} [extension]
 */
const downloadFile = async (url, name, extension) => {
  const response = await HTTP.get(url, {
    responseType: 'blob'
  })
  const fileName = getDownloadFileName(
    response.headers['content-disposition'],
    name,
    extension
  )

  const objectUrl = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement('a')

  link.href = objectUrl
  link.setAttribute('download', fileName)
  link.click()
}

/**
 * @param   {string} [headerContentDisposition]
 * @param   {string} [name]
 * @param   {string} [extension]
 *
 * @returns {string}
 */
const getDownloadFileName = (headerContentDisposition, name, extension) => {
  if (headerContentDisposition) {
    return JSON.parse(headerContentDisposition.split(';')[1].split('=')[1])
  } else {
    const fileName = name || 'file'
    const extensionWithDotOptional = extension ? `.${extension}` : ''

    return `${fileName}${extensionWithDotOptional}`
  }
}

/**
 * @param   {number}       projectId
 *
 * @returns {AxiosPromise}
 */
const fetchBoards = projectId => {
  return HTTP.get(`/projects/${projectId}/boards/`)
}

/**
 * @param   {number}       projectId
 * @param   {number}       boardId
 *
 * @returns {AxiosPromise}
 */
const fetchBoard = (projectId, boardId) => {
  return HTTP.get(`/projects/${projectId}/boards/${boardId}/`)
}

/**
 * @param   {number}       styleId
 *
 * @returns {AxiosPromise}
 */
const fetchStyle = styleId => {
  return HTTP.get(`/styles/${styleId}/`)
}

/**
 * @param   {number}       projectId
 *
 * @returns {AxiosPromise}
 */
const fetchOptions = projectId => {
  return HTTP.get(`/projects/${projectId}/options/`)
}

/// ///////////////////////////////////////////////
// Collection overview
/// ///////////////////////////////////////////////

let cancelFunction = null
let previousCalledPage = -1

/**
 * @param   {number}       groupId
 * @param   {number}       seasonId
 * @param   {number}       projectId
 * @param   {number}       page
 * @param   {number}       limit
 * @param   {object}       filterQuery
 * @param   {string}       search
 * @param   {string}       order
 *
 * @returns {AxiosPromise}
 */
const fetchOptionsPaginated = (
  groupId,
  seasonId,
  projectId,
  page,
  limit,
  filterQuery,
  search,
  order
) => {
  return fetchItemsPaginated(
    '/options/',
    groupId,
    seasonId,
    projectId,
    page,
    limit,
    filterQuery,
    search,
    order
  )
}

/**
 * @param   {number}       groupId
 * @param   {number}       seasonId
 * @param   {number}       projectId
 * @param   {number}       page
 * @param   {number}       limit
 * @param   {object}       filterQuery
 * @param   {string}       search
 * @param   {string}       order
 *
 * @returns {AxiosPromise}
 */
const fetchStylesPaginated = (
  groupId,
  seasonId,
  projectId,
  page,
  limit,
  filterQuery,
  search,
  order
) => {
  return fetchItemsPaginated(
    '/styles/',
    groupId,
    seasonId,
    projectId,
    page,
    limit,
    filterQuery,
    search,
    order
  )
}

/**
 * @param   {string}              endpoint
 * @param   {number}              groupId
 * @param   {number}              seasonId
 * @param   {number}              projectId
 * @param   {number}              page
 * @param   {number}              limit
 * @param   {object}              filterQuery
 * @param   {string}              search
 * @param   {string}              order
 *
 * @returns {AxiosPromise | null}
 */
const fetchItemsPaginated = async (
  endpoint,
  groupId,
  seasonId,
  projectId,
  page,
  limit,
  filterQuery,
  search,
  order
) => {
  if (page <= previousCalledPage) {
    if (cancelFunction) {
      cancelFunction.cancel()
      cancelFunction = null
    }
  }

  if (!cancelFunction) {
    cancelFunction = axios.CancelToken.source()
  }

  previousCalledPage = page

  const queryParameters = {
    group_id: groupId,
    season_id: seasonId,
    project_id: projectId,
    search,
    limit,
    offset: (page - 1) * limit,
    custom_order: order,
    ...filterQuery
  }

  try {
    return await HTTP.get(endpoint, {
      params: queryParameters,
      cancelToken: cancelFunction.token
    })
  } catch (error) {
    // Handles cancel event has not an error
    if (axios.isCancel(error)) {
      return { canceled: true }
    }

    console.warn(error)

    // Errors handled by catch statement will not log on sentry
    // So we send them manually
    Sentry.captureException(error)

    return error
  }
}

/// ///////////////////////////////////////////////
// End Collection overview
/// ///////////////////////////////////////////////

/**
 * @param   {string}       name
 * @param   {number}       groupId
 * @param   {number}       seasonId
 *
 * @returns {AxiosPromise}
 */
const createProject = (name, groupId, seasonId) => {
  return HTTP.post('/projects/', {
    name,
    group_id: groupId,
    season_id: seasonId
  })
}

/**
 * @param   {string}       projectId
 * @param   {string}       layout
 * @param   {string}       name
 * @param   {string}       index
 *
 * @returns {AxiosPromise}
 */
const createBoard = (projectId, layout, name, index) => {
  return HTTP.post(`/projects/${projectId}/boards/`, {
    name,
    layout: layout,
    index
  })
}

/**
 * @param   {string}       projectId
 * @param   {string}       boardId
 * @param   {string}       name
 * @param   {string}       layout
 * @param   {string}       index
 * @param   {object}       meta
 *
 * @returns {AxiosPromise}
 */
const createStyle = (
  projectId,
  boardId,
  name,
  layout,
  index,
  meta = {}
) => {
  const payload = {
    name: name,
    layout: layout,
    index: index,
    ...meta
  }

  return HTTP.post(`/projects/${projectId}/boards/${boardId}/styles/`, payload)
}

/**
 * @param   {number}          styleId
 * @param   {number}          index
 * @param   {object | string} file
 * @param   {object}          meta
 *
 * @returns {AxiosPromise}
 */
const createOption = (styleId, index, file, meta = {}) => {
  const formData = new FormData()

  if (typeof file === 'string') {
    formData.append('url', file)
  }

  if (typeof file === 'object') {
    formData.append('file', file)
  }

  formData.append('type', 'colorway')

  for (const prop in meta) {
    if (Object.prototype.hasOwnProperty.call(meta, prop)) {
      formData.append(prop, meta[prop])
    }
  }

  formData.append('index', index)

  return HTTP.post(`/styles/${styleId}/options/`, formData)
}

/**
 * @param   {number}       styleId
 * @param   {number}       id
 *
 * @returns {AxiosPromise}
 */
const deleteOption = (styleId, id) => {
  return HTTP.delete(`/projects/1/boards/1/styles/${styleId}/options/${id}/`)
}

/**
 * @param   {number}       projectId
 * @param   {boolean}      visibleToSales
 *
 * @returns {AxiosPromise}
 */
const updateProject = (projectId, visibleToSales) => {
  const projectData = {}

  if (visibleToSales === true || visibleToSales === false) {
    projectData.visible_to_sales = visibleToSales
  }

  return HTTP.patch(`/projects/${projectId}/`, projectData)
}

/**
 * @param   {number}       projectId
 * @param   {number}       boardId
 * @param   {string}       layout
 * @param   {string}       name
 * @param   {number}       index
 *
 * @returns {AxiosPromise}
 */
const updateBoard = (projectId, boardId, layout, name, index) => {
  const boardData = {}

  if (name) {
    boardData.name = name
  }

  if (layout) {
    boardData.layout = layout
  }

  if (index || index === 0) {
    boardData.index = index
  }

  return HTTP.patch(`/projects/${projectId}/boards/${boardId}/`, boardData)
}

/**
 * @param   {number}       styleId
 * @param   {string}       layout
 * @param   {string}       name
 * @param   {string}       notes
 * @param   {number}       index
 * @param   {number}       newBoardId
 * @param   {object}       meta
 * @param   {object}       attributes
 *
 * @returns {AxiosPromise}
 */
const updateStyle = (
  styleId,
  name,
  notes,
  layout,
  index,
  newBoardId,
  meta = {},
  attributes
) => {
  const styleData = {
    ...meta
  }

  if (name) {
    styleData.name = name
  }

  if (notes || notes === '') {
    styleData.notes = notes
  }

  if (layout) {
    styleData.layout = layout
  }

  if (index || index === 0) {
    styleData.index = index
  }

  if (newBoardId) {
    styleData.board_id = newBoardId
  }

  if (attributes) {
    styleData.attributes = attributes
  }

  return HTTP.patch(`/styles/${styleId}/`, styleData)
}

/**
 * @param   {number}          styleId
 * @param   {number}          optionId
 * @param   {number}          index
 * @param   {object}          meta
 * @param   {object | string} file
 *
 * @returns {AxiosPromise}
 */
const updateOption = (styleId, optionId, index, meta, file) => {
  const formData = new FormData()

  if (file) {
    if (typeof file === 'string') {
      formData.append('url', file)
    }

    if (typeof file === 'object') {
      formData.append('file', file)
    }
  }

  for (const prop in meta) {
    if (Object.prototype.hasOwnProperty.call(meta, prop)) {
      formData.append(prop, meta[prop])
    }
  }

  if (index || index === 0) {
    formData.append('index', index)
  }

  return HTTP.patch(`/styles/${styleId}/options/${optionId}/`, formData)
}

/**
 * @param   {number}       projectId
 * @param   {number}       boardId
 *
 * @returns {AxiosPromise}
 */
const deleteBoard = (projectId, boardId) => {
  return HTTP.delete(`/projects/${projectId}/boards/${boardId}/`)
}

/**
 * @param   {number}       projectId
 *
 * @returns {AxiosPromise}
 */
const deleteProject = projectId => {
  return HTTP.delete(`/projects/${projectId}/`)
}

/**
 * @param   {number}       projectId
 * @param   {number}       boardId
 * @param   {string}       boardName
 *
 * @returns {AxiosPromise}
 */
const updateBoardName = (projectId, boardId, boardName) => {
  return HTTP.patch(`/projects/${projectId}/boards/${boardId}/`, {
    name: boardName
  })
}

/**
 * @param   {number}       projectId
 * @param   {number}       boardId
 * @param   {string}       boardLabel
 *
 * @returns {AxiosPromise}
 */
const updateBoardLabel = (projectId, boardId, boardLabel) => {
  return HTTP.patch(`/projects/${projectId}/boards/${boardId}/`, {
    label: boardLabel
  })
}

/**
 * @param   {number}              styleId
 * @param   {object | string}     label
 *
 * @returns {AxiosPromise | null}
 */
const updateStyleLabel = async (styleId, label) => {
  const formData = new FormData()

  if ((label && typeof label === 'object') || label === '') {
    formData.append('label', label)
  }

  if (cancelFunction) {
    cancelFunction.cancel()
    cancelFunction = null
  }

  if (!cancelFunction) {
    cancelFunction = axios.CancelToken.source()
  }

  try {
    return await HTTP.patch(`/styles/${styleId}/`, formData, {
      cancelToken: cancelFunction.token
    })
  } catch (error) {
    // If error, the error will be caught in the error callback
    // Returning null to prevent error related to the ui
    if (!axios.isCancel(error)) {
      console.warn(error)
    }

    return null
  }
}

/**
 * @param   {number}       projectId
 * @param   {number}       boardId
 * @param   {number}       styleId
 * @param   {string}       name
 *
 * @returns {AxiosPromise}
 */
const updateStyleName = (projectId, boardId, styleId, name) => {
  return HTTP.patch(
    `/projects/${projectId}/boards/${boardId}/styles/${styleId}/`,
    {
      name
    }
  )
}

/**
 * @param   {number}       projectId
 * @param   {number}       boardId
 * @param   {number}       index
 *
 * @returns {AxiosPromise}
 */
const updateBoardIndex = (projectId, boardId, index) => {
  return HTTP.patch(`/projects/${projectId}/boards/${boardId}/`, {
    index
  })
}

/**
 * @param   {number}       styleId
 *
 * @returns {AxiosPromise}
 */
const deleteStyle = styleId => {
  return HTTP.delete(`/styles/${styleId}/`)
}

/**
 * @param   {number}        styleId
 * @param   {string}        comment
 * @param   {number | null} rating
 *
 * @returns {AxiosPromise}
 */
const createFeedback = (styleId, comment, rating) => {
  const payload = {}

  if (comment || comment === null) {
    payload.comment = comment
  }

  if (rating || rating === 0 || rating === null) {
    payload.rating = rating
  }

  return HTTP.post(`/styles/${styleId}/feedbacks/`, payload)
}

/**
 * @param   {number}       styleId
 * @param   {number}       feedbackId
 * @param   {string}       comment
 * @param   {number}       rating
 *
 * @returns {AxiosPromise}
 */
const updateFeedback = (styleId, feedbackId, comment, rating) => {
  const payload = {}

  if (comment) {
    payload.comment = comment
  }

  if (comment === '' || comment === null) {
    payload.comment = null
  }

  if (rating || rating === 0 || rating === null) {
    payload.rating = rating
  }

  return HTTP.patch(`/styles/${styleId}/feedbacks/${feedbackId}/`, payload)
}

/**
 * @param   {number}          styleId
 * @param   {number}          index
 * @param   {object | string} file
 *
 * @returns {AxiosPromise}
 */
const createDetail = (styleId, index, file) => {
  const formData = new FormData()

  if (typeof file === 'string') {
    formData.append('url', file)
  }

  if (typeof file === 'object') {
    formData.append('file', file)
  }

  formData.append('index', index)

  return HTTP.post(`/styles/${styleId}/details/`, formData)
}

/**
 * @param   {number}       styleId
 * @param   {number}       detailId
 * @param   {number}       index
 *
 * @returns {AxiosPromise}
 */
const updateDetail = (styleId, detailId, index) => {
  return HTTP.patch(`/styles/${styleId}/details/${detailId}/`, {
    index
  })
}

/**
 * @param   {number}       styleId
 * @param   {number}       detailId
 *
 * @returns {AxiosPromise}
 */
const deleteDetail = (styleId, detailId) => {
  return HTTP.delete(`/styles/${styleId}/details/${detailId}/`)
}

/**
 * @param   {number}       groupId
 * @param   {number}       seasonId
 * @param   {boolean}      [assignedOnly=false]
 *
 * @returns {AxiosPromise}
 */
const fetchCustomTags = (groupId, seasonId, assignedOnly = false) => {
  return HTTP.get('/custom-tags/', {
    params: {
      group_id: groupId,
      season_id: seasonId,
      assigned_only: assignedOnly
    }
  })
}

/**
 * @param   {number}       styleId
 * @param   {string}       text
 *
 * @returns {AxiosPromise}
 */
const createStyleCustomTag = (styleId, text) => {
  return HTTP.post(`/styles/${styleId}/custom-tags/`, {
    text
  })
}

/**
 * @param   {number}       styleId
 * @param   {number}       tagId
 *
 * @returns {AxiosPromise}
 */
const deleteStyleCustomTag = (styleId, tagId) => {
  return HTTP.delete(`/styles/${styleId}/custom-tags/${tagId}/`)
}

/**
 * @param   {string}       field
 *
 * @returns {AxiosPromise}
 */
const fetchMetaFilters = field => {
  return HTTP.get(`/tags/${getFetchMetaFiltersEndpoint(field)}/`)
}

/**
 * @param   {string} field
 *
 * @returns {string}
 */
const getFetchMetaFiltersEndpoint = field => {
  if (fetchMetaFiltersEndpointMap[field]) {
    return fetchMetaFiltersEndpointMap[field]
  } else {
    throw new Error(`Unexpected meta field ${field}.`)
  }
}

/**
 * @param   {string}       field
 * @param   {string}       value
 * @param   {number}       styleId
 *
 * @returns {AxiosPromise}
 */
const createStyleMetaFilter = (field, value, styleId) => {
  return HTTP.post(
    `/styles/${styleId}/tags/${getCreateMetaFilterEndpoint(field)}`,
    {
      name: value
    }
  )
}

/**
 * @param   {string} field
 *
 * @returns {string}
 */
const getCreateMetaFilterEndpoint = field => {
  if (createMetaFilterEndpointMap[field]) {
    return createMetaFilterEndpointMap[field]
  } else {
    throw new Error(`Unexpected meta field ${field}.`)
  }
}

const ApiService = {
  getApiBaseUrl,
  setCallbacks,
  fetchUserInfo,
  fetchSeasons,
  fetchInvites,
  createInvite,
  deleteInvite,
  fetchGroups,
  createProject,
  fetchProject,
  downloadProjectFeedbackCSV,
  getDownloadFileName,
  fetchStyle,
  fetchOptions,
  fetchOptionsPaginated,
  fetchStylesPaginated,
  updateProject,
  updateBoard,
  updateStyle,
  updateOption,
  updateBoardIndex,
  updateBoardName,
  updateBoardLabel,
  deleteProject,
  fetchProjects,
  createBoard,
  fetchBoards,
  fetchBoard,
  createStyle,
  createOption,
  deleteOption,
  updateStyleLabel,
  updateStyleName,
  deleteStyle,
  deleteBoard,
  createFeedback,
  updateFeedback,
  createDetail,
  updateDetail,
  deleteDetail,
  fetchCustomTags,
  createStyleCustomTag,
  deleteStyleCustomTag,
  fetchMetaFilters,
  createStyleMetaFilter,
  ...DesignHub,
  HTTP
}

export default ApiService
